/**
 * Who may send which event — checked by the reducer, so on every device and on the server.
 *
 * The screens already hide what a role may not do, but a screen is only one way in: an old
 * client, a script against the sync endpoint, or a phone with a patched app could send the
 * owner's events as anybody. Before this nothing below the screens looked at who was acting, so
 * any registered device could change prices, receive stock, count it, pay itself, add a user or
 * change a setting, and every one of those went into the books.
 *
 * Who is acting is the event's `actorId`, looked up in the users table as it stands at that
 * point in the log. Replaying, a person's events are judged by the role they had then: an owner
 * made a cashier later keeps the prices they set as owner.
 *
 * Only the owner's work is refused here. What happens at the counter — a sale, a payment, a
 * till — is never turned away for who rang it: the goods have left by the time the event
 * arrives, and refusing it would lose the record of a sale that happened (the same reason the
 * fiado limit is checked at the counter). Those are for the owner's oversight to catch.
 */

import { can, esCamion, fechaEnNicaragua, type AnyDomainEvent, type EventType, type Permission } from '@abastece/shared';
import type { Reader, Rejection, Usuario } from './state.js';

/** The owner's events, and what each takes — any one of these. Unlisted events are the counter's. */
const PERMISO_DE_EVENTO: Partial<Record<EventType, readonly Permission[]>> = {
  'usuario.creado': ['gestionar_usuarios'],
  'usuario.actualizado': ['gestionar_usuarios'],
  'usuario.pin_cambiado': ['gestionar_usuarios'],
  'usuario.desactivado': ['gestionar_usuarios'],
  'usuario.reactivado': ['gestionar_usuarios'],
  'dispositivo.registrado': ['gestionar_usuarios'],
  'dispositivo.revocado': ['gestionar_usuarios'],
  'dispositivo.reactivado': ['gestionar_usuarios'],
  'config.cambiada': ['configurar'],

  'producto.creado': ['editar_productos'],
  'producto.actualizado': ['editar_productos'],
  'producto.desactivado': ['editar_productos'],
  'producto.reactivado': ['editar_productos'],
  'unidad_venta.creada': ['editar_productos'],
  'unidad_venta.actualizada': ['editar_productos'],
  'unidad_venta.eliminada': ['editar_productos'],
  // What the products sell for, and the lists, the default one being what the till charges: the
  // owner's (2026-10-13), where the products themselves are a cashier's too. Nobody else had set a
  // price on the live server, so judging the log by this changes nothing in it (checked 2026-10-13).
  'lista_precio.creada': ['editar_precios'],
  'lista_precio.actualizada': ['editar_precios'],
  'lista_precio.eliminada': ['editar_precios'],
  'precio.fijado': ['editar_precios'],
  'oferta.creada': ['editar_precios'],
  'oferta.terminada': ['editar_precios'],

  'proveedor.creado': ['recibir_compras'],
  // What the shop knows of a supplier, changed: the owner's (2026-10-16). No screen had changed one
  // before, and the live server had none, so judging the log by this changes nothing in it.
  'proveedor.actualizado': ['editar_proveedores'],
  'proveedor.desactivado': ['recibir_compras'],
  'compra.registrada': ['recibir_compras'],
  'compra.anulada': ['recibir_compras'],
  // What a correction changes is money already counted: a cashier lowering what she paid after her
  // corte booked a surplus that took her shortage away (CONTROLES item 13). The owner's.
  'compra.costo_corregido': ['configurar'],
  // What each unit cost, in the stock and in the sales that took it: the owner's, as the cost is.
  'compra.cantidad_corregida': ['configurar'],
  // Money already counted, moved from where the purchase said it came to where it did: the owner's.
  'compra.forma_pago_corregida': ['configurar'],
  // A till's count put right, and the money that follows it: the owner's, as the books are.
  'caja.apertura_corregida': ['configurar'],
  'caja.corte_corregido': ['configurar'],
  // Buying airtime is a purchase; counting it is a count.
  'recarga.saldo_comprado': ['recibir_compras'],
  'recarga.saldo_contado': ['ajustar_inventario'],
  'compra.devuelta': ['recibir_compras'],
  // Paying a purchase still owed is done in «Gastos y pagos», which asks for this before its
  // «Abonar», as an expense's is (`gasto.pagado`): not whoever receives purchases (2026-10-09).
  'compra.pagada': ['configurar'],
  // What was owed before the shop kept its books here: both go against Capital. The shop's own
  // debt is the owner's, entered in «Gastos y pagos», since a cashier receives the purchases too
  // (2026-10-09).
  'compra.saldo_inicial': ['configurar'],
  'cliente.saldo_inicial': ['dar_credito'],
  // Giving up on what a client owes, and undoing it: whoever decides who gets fiado.
  'incobrable.registrado': ['dar_credito'],
  'incobrable.anulado': ['dar_credito'],

  // What was on the shelf before the books, at what it cost, against Capital: it sets a product's
  // stock and its average cost to whatever it says. The owner's opening day, not a cashier's count,
  // which books the difference as a loss or a gain (2026-10-09).
  'inventario.saldo_inicial': ['configurar'],
  // What the stock is worth, set by hand: the owner's (2026-10-13).
  'inventario.costo_corregido': ['configurar'],
  'inventario.conteo': ['ajustar_inventario'],
  'inventario.salida': ['ajustar_inventario'],
  'inventario.reempaque': ['reempacar'],
  'inventario.traslado': ['ajustar_inventario', 'cargar_camion'],
  'jornada.liquidada': ['liquidar_ruta'],

  'efectivo.recibido': ['recibir_efectivo'],
  'efectivo.arqueo': ['recibir_efectivo'],
  'cuenta.conciliada': ['recibir_efectivo'],
  // Whoever holds the safe takes the money to the bank, voids a deposit that was not, and goes
  // over the week.
  'efectivo.depositado': ['recibir_efectivo'],
  'efectivo.deposito_anulado': ['recibir_efectivo'],
  'efectivo.sacado_del_banco': ['recibir_efectivo'],
  // Money moved between the owner's accounts, and such a move voided: «Mover dinero» asks for this,
  // and the books did not, so anybody's could move the bank's money (2026-10-20). The live server had
  // none, so judging the log by this changes nothing in it.
  'banco.transferido': ['recibir_efectivo'],
  'banco.transferencia_anulada': ['recibir_efectivo'],
  'semana.cuadrada': ['recibir_efectivo'],
  'pago.confirmado': ['confirmar_transferencia'],
  'pago.rechazado': ['confirmar_transferencia'],

  'empleado.contratado': ['gestionar_planilla'],
  'empleado.actualizado': ['gestionar_planilla'],
  'empleado.liquidado': ['gestionar_planilla'],
  'empleado.adelanto': ['gestionar_planilla'],
  'empleado.vacaciones': ['gestionar_planilla'],
  'planilla.corrida': ['gestionar_planilla'],
  'planilla.pagada': ['gestionar_planilla'],
  'planilla.corregida': ['gestionar_planilla'],
  'cotizaciones.pagadas': ['gestionar_planilla'],
  'aguinaldo.pagado': ['gestionar_planilla'],
  'aguinaldo.corregido': ['gestionar_planilla'],
  // What «Gastos» itself asks for before it records one. Recording one is also a driver's, on
  // route (`REVISADOS`).
  'gasto.anulado': ['configurar'],
  'gasto.pagado': ['configurar'],
  'dueno.retiro': ['configurar'],
  'dueno.aporte': ['configurar'],
  // Saying what a shortage was takes it off whoever had the till: the owner's word, as voiding is.
  'faltante.explicado': ['configurar'],
  'compra.pago_anulado': ['configurar'],
  'gasto.pago_anulado': ['configurar'],
  'dueno.movimiento_anulado': ['configurar'],
};

/** Not the owner's alone, but with something in them that is: a client's list, a void. */
const REVISADOS: ReadonlySet<EventType> = new Set<EventType>([
  'cliente.creado',
  'cliente.actualizado',
  'venta.anulada',
  'devolucion.registrada',
  'abono.anulado',
  'gasto.registrado',
]);

const rechazo = (event: AnyDomainEvent, reason: string): Rejection => ({ eventId: event.id, reason });

const esDueno = (u: Usuario | undefined): u is Usuario => u !== undefined && u.activo && u.rol === 'dueno';

/**
 * Null when the actor may send this event; otherwise the reason it is refused, for the
 * bitácora's list of rejections.
 */
export async function comprobarPermiso(event: AnyDomainEvent, read: Reader): Promise<Rejection | null> {
  // Most of the log is the counter's: a sign-in, a sale. Those do not look anybody up.
  if (!PERMISO_DE_EVENTO[event.type] && !REVISADOS.has(event.type)) return null;
  const actor = await read.usuario(event.actorId);

  /*
   * The very first person in a shop creates themselves, as its owner: the install on the server,
   * the practice shop on a phone. Only while there is nobody at all — afterwards a made-up
   * account creating itself as owner would be a way in.
   */
  if (event.type === 'usuario.creado' && !actor && !(await read.hayUsuarios())) {
    const { usuarioId, rol } = event.payload;
    return usuarioId === event.actorId && rol === 'dueno'
      ? null
      : rechazo(event, 'La primera persona de una tienda es su dueño, y se crea a sí misma');
  }

  /*
   * Only as whoever is using the device it came from. A device's token proves which device sent
   * an event and nothing about who: the name on it is the device's word. So the owner's decisions,
   * and the undoing that takes the owner, count only from a device the owner is using, as the
   * device's own sign-ins say (`Dispositivo.usuarioActual`). Anybody else has to put a sign-in in
   * the owner's name in the log first, which «Control» shows the owner, device by device.
   */
  const equipo = await read.dispositivo(event.deviceId);
  if (equipo?.usuarioActual && equipo.usuarioActual !== event.actorId) {
    return rechazo(event, `${actor?.nombre ?? 'Esa persona'} no es quien está usando este equipo`);
  }

  // Anybody may change their own PIN; somebody else's is the owner's to change.
  if (event.type === 'usuario.pin_cambiado' && event.payload.usuarioId === event.actorId) {
    return actor?.activo ? null : rechazo(event, 'Quien cambia el PIN ya no está activo');
  }

  const permisos = PERMISO_DE_EVENTO[event.type];
  if (permisos && !permitido(actor, permisos)) {
    return rechazo(event, actor ? `${actor.nombre} no puede hacer esto: es del dueño` : 'Quien lo hizo no es de la tienda');
  }

  switch (event.type) {
    // A new product's prices are prices too: a cashier makes it without them, for the owner to price.
    case 'producto.creado':
      return event.payload.precios.length > 0 && !permitido(actor, ['editar_precios'])
        ? rechazo(event, 'Los precios los pone el dueño')
        : null;

    // What a presentation holds is what its price buys: a quintal of 110 pounds at the price of 100
    // is a price lowered (2026-10-13). The owner's, as the price is.
    case 'unidad_venta.actualizada':
      return event.payload.factor !== undefined && !permitido(actor, ['editar_precios'])
        ? rechazo(event, 'Lo que lleva una presentación lo cambia el dueño: cambia lo que compra su precio')
        : null;

    case 'cliente.creado':
    case 'cliente.actualizado': {
      // What a client pays and how much they may owe are the owner's, on any client.
      const p = event.payload;
      const conLista = event.type === 'cliente.creado' ? p.listaId !== null : p.listaId !== undefined;
      const conLimite = event.type === 'cliente.creado' ? p.limiteCredito !== null : p.limiteCredito !== undefined;
      if (conLista && !permitido(actor, ['elegir_lista'])) {
        return rechazo(event, 'La lista de precios de un cliente la pone el dueño');
      }
      if (conLimite && !permitido(actor, ['dar_credito'])) {
        return rechazo(event, 'El límite de fiado de un cliente lo pone el dueño');
      }
      return null;
    }

    case 'venta.anulada':
    case 'devolucion.registrada':
    case 'abono.anulado':
      return comprobarDeshacer(event, actor, read);

    case 'gasto.registrado': {
      if (permitido(actor, ['configurar'])) return null;
      // A driver pays for fuel out of the route's cash, and that is the only expense that is
      // theirs: from a truck's till, which the reducer then checks is open and holds enough, and
      // all of it from there. One paid in parts is the owner's.
      const { sesionId, pagos } = event.payload;
      const sesion = sesionId && !pagos && permitido(actor, ['gasto_de_ruta']) ? await read.sesionCaja(sesionId) : undefined;
      if (sesion && esCamion(sesion.ubicacion)) return null;
      return rechazo(event, actor ? `${actor.nombre} no puede hacer esto: es del dueño` : 'Quien lo hizo no es de la tienda');
    }

    default:
      return null;
  }
}

function permitido(actor: Usuario | undefined, permisos: readonly Permission[]): boolean {
  return actor !== undefined && actor.activo && permisos.some((p) => can(actor.rol, p));
}

/**
 * Undoing something that took money — a void, a refund, an abono voided — and who has to have
 * said yes to it.
 *
 * Within the shift still open, whoever is signed in does it in their own name, unless the owner
 * asked for their PIN on everything (`anularConDueno`). From before that, it takes the owner:
 * the till it touched has been counted and its money has gone to general cash, so "the customer
 * paid yesterday; void it today and keep the money" balances every corte there is. The owner is
 * then the actor, or the owner whose PIN let it through (`autorizadoPor`). An `autorizadoPor`
 * that is not an active owner is refused either way: somebody put a name there that cannot say
 * yes.
 */
async function comprobarDeshacer(
  event: Extract<AnyDomainEvent, { type: 'venta.anulada' | 'devolucion.registrada' | 'abono.anulado' }>,
  actor: Usuario | undefined,
  read: Reader,
): Promise<Rejection | null> {
  const permiso: Permission = event.type === 'devolucion.registrada' ? 'devolver' : 'anular_venta';
  if (!permitido(actor, [permiso])) {
    return rechazo(event, actor ? `${actor.nombre} no puede anular ni devolver` : 'Quien lo hizo no es de la tienda');
  }

  const { autorizadoPor } = event.payload;
  const autoriza = autorizadoPor ? await read.usuario(autorizadoPor) : undefined;
  if (autorizadoPor && !esDueno(autoriza)) {
    return rechazo(event, 'Lo autorizó alguien que no es dueño');
  }
  if (esDueno(actor) || autoriza) return null;

  if ((await read.config()).anularConDueno) {
    return rechazo(event, 'En esta tienda, anular y devolver pide el PIN del dueño');
  }
  return (await deOtroTurno(event, read))
    ? rechazo(event, 'Es de antes de este turno: deshacerlo pide el PIN del dueño')
    : null;
}

/**
 * From before the shift still open: the till it went into has closed. An abono with no till,
 * paid by transfer or wallet, has no shift — for it, another day is before.
 *
 * Whatever cannot be found is left to the reducer, which refuses it with the better reason.
 */
async function deOtroTurno(
  event: Extract<AnyDomainEvent, { type: 'venta.anulada' | 'devolucion.registrada' | 'abono.anulado' }>,
  read: Reader,
): Promise<boolean> {
  if (event.type === 'abono.anulado') {
    const abono = await read.abono(event.payload.abonoId);
    if (!abono) return false;
    if (abono.sesionId === null) return abono.fecha !== fechaEnNicaragua(event.clientTs);
    return (await read.sesionCaja(abono.sesionId))?.estado !== 'abierta';
  }
  const venta = await read.venta(event.payload.ventaId);
  if (!venta) return false;
  return (await read.sesionCaja(venta.sesionId))?.estado !== 'abierta';
}
